import { useNavigate, useSearch } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BeheerderShell } from "@/components/BeheerderShell";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { OndersteunerReistijden } from "@/features/reistijden/OndersteunerReistijden";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * De reistijden van de ondersteuners: één seizoen, één wedstrijddag tegelijk.
 *
 * The persoon-axis twin of `ReistijdenKorpsenPage`, reached from the `/reistijden` landing page.
 */
export function ReistijdenOndersteunersPage() {
  const { t } = useTranslation([
    "paginas",
    "navigatie",
    "common",
    "reistijden",
  ]);
  const { data: seizoenen } = useSeizoenen();
  const {
    seizoen: seizoenId,
    dag: dagId,
    pagina,
  } = useSearch({
    from: "/reistijden/ondersteuners",
  });
  const navigate = useNavigate({ from: "/reistijden/ondersteuners" });

  // A seizoen named in the URL wins; otherwise the one last picked on the Seizoenen screen;
  // otherwise the running one - the same fallback the korps screen applies.
  const seizoen = kiesSeizoen(seizoenen, seizoenId);

  return (
    <BeheerderShell
      titel={t("navigatie:paginas.reistijdenOndersteuners.label")}
      uitleg={
        seizoen ? (
          <>
            {t("common:seizoen.lopend", { naam: seizoen.name })}{" "}
            {t("reistijdenOndersteuners.uitleg")}
          </>
        ) : (
          <>
            {t("common:seizoen.geenBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </>
        )
      }
    >
      {/* #172 asked for the opgaven here. They live on the wedstrijddag, which is the screen
          that holds the dag; this one exists for a reistijd that reverts silently (ADR-0013). */}
      {seizoen && dagId && (
        <p className="mb-4 text-sm">
          <Link
            className="jb-link"
            to="/wedstrijddagen"
            search={{ seizoen: seizoen.id, dag: dagId, tab: "ondersteuners" }}
          >
            {t("reistijden:dagen.naarDeelname")}
          </Link>
        </p>
      )}

      {seizoen && (
        <OndersteunerReistijden
          seizoen={seizoen}
          dagId={dagId}
          // `pagina` resets on a new dag; `punt` survives it, like the korps screen and the
          // wedstrijddag screen's own filters.
          onKies={(id) =>
            void navigate({
              ...keepScrollPosition,
              search: (previous) => ({
                ...previous,
                seizoen: seizoen.id,
                dag: id,
                pagina: undefined,
              }),
            })
          }
          pagina={pagina}
          onPaginaChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, pagina: next }),
              ...keepScrollPosition,
            })
          }
        />
      )}
    </BeheerderShell>
  );
}
